/**
 * Agent Course Browser Component
 * Browse universities and courses with filters for Intake, University, Country
 * Shows commission percentages for each university
 */
import { useState, useMemo, useCallback, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Search, Building, MapPin, GraduationCap, Filter, 
  Calendar, DollarSign, Percent, BookOpen, ExternalLink,
  TrendingUp, Globe, X, Loader2
} from 'lucide-react';
import { coursesAPI } from '@/lib/api';

export const CourseBrowser = ({ universities, courses: initialCourses, onApplyForStudent, selectedStudent }) => {
  const [viewMode, setViewMode] = useState('courses');
  const [searchTerm, setSearchTerm] = useState('');
  const [countryFilter, setCountryFilter] = useState('all');
  const [universityFilter, setUniversityFilter] = useState('all');
  const [intakeFilter, setIntakeFilter] = useState('all');
  const [fieldFilter, setFieldFilter] = useState('all');
  const [levelFilter, setLevelFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [courses, setCourses] = useState(initialCourses || []);
  const [searching, setSearching] = useState(false);
  const [totalLabel, setTotalLabel] = useState('');

  // Server-side search when search term changes
  useEffect(() => {
    if (!searchTerm && !countryFilter?.replace('all','') && !levelFilter?.replace('all','')) {
      setCourses(initialCourses || []);
      setTotalLabel('');
      return;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const params = {};
        if (searchTerm) params.search = searchTerm;
        if (countryFilter && countryFilter !== 'all') params.country = countryFilter;
        if (levelFilter && levelFilter !== 'all') params.degree_level = levelFilter;
        const res = await coursesAPI.getAll(params);
        setCourses(res.data || []);
        setTotalLabel(`${(res.data || []).length} results`);
      } catch (e) {
        console.error('Search failed:', e);
      } finally {
        setSearching(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm, countryFilter, levelFilter]);

  // Update when parent courses change
  useEffect(() => {
    if (!searchTerm) setCourses(initialCourses || []);
  }, [initialCourses]);

  // Normalize country names (UK/United Kingdom -> United Kingdom)
  const normalizeCountry = (country) => {
    if (!country) return country;
    const normalized = country.trim();
    if (normalized.toLowerCase() === 'uk' || normalized.toLowerCase() === 'u.k.' || normalized.toLowerCase() === 'u.k') {
      return 'United Kingdom';
    }
    return normalized;
  };

  // Create university lookup map
  const universityMap = useMemo(() => {
    const map = {};
    universities.forEach(uni => {
      map[uni.id] = uni;
    });
    return map;
  }, [universities]);

  // Get unique filter options (with country normalization)
  const filterOptions = useMemo(() => {
    const countriesSet = new Set();
    universities.forEach(u => {
      if (u.country) countriesSet.add(normalizeCountry(u.country));
    });
    const countries = [...countriesSet].sort();
    const fields = [...new Set(courses.map(c => c.field_of_study).filter(Boolean))].sort();
    
    // Normalize degree levels to proper capitalization and remove duplicates
    const normalizeLevel = (level) => {
      if (!level) return null;
      const l = level.toLowerCase().trim();
      if (l === 'bachelors' || l === 'bachelor') return 'Bachelors';
      if (l === 'masters' || l === 'master') return 'Masters';
      if (l === 'phd' || l === 'doctorate' || l === 'doctoral') return 'PhD';
      if (l === 'foundation') return 'Foundation';
      if (l === 'undergraduate') return 'Undergraduate';
      if (l === 'pre-masters') return 'Pre-Masters';
      // Capitalize first letter for any other level
      return level.charAt(0).toUpperCase() + level.slice(1).toLowerCase();
    };
    
    const rawLevels = courses.map(c => normalizeLevel(c.degree_level || c.level)).filter(Boolean);
    const levels = [...new Set(rawLevels)].sort();
    
    // Get all unique intakes from universities
    const intakes = new Set();
    universities.forEach(uni => {
      if (uni.available_intakes?.length) {
        uni.available_intakes.forEach(intake => intakes.add(intake));
      }
    });
    // Also get intakes from courses
    courses.forEach(course => {
      if (course.intake_dates) {
        const dates = Array.isArray(course.intake_dates) 
          ? course.intake_dates 
          : course.intake_dates.split(',').map(d => d.trim());
        dates.forEach(d => intakes.add(d));
      }
    });
    
    return {
      countries,
      fields,
      levels,
      intakes: [...intakes].sort()
    };
  }, [universities, courses]);

  // Filter universities (with country normalization)
  const filteredUniversities = useMemo(() => {
    return universities.filter(uni => {
      // Search term filter
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        if (!uni.name.toLowerCase().includes(term) && 
            !uni.city?.toLowerCase().includes(term) &&
            !uni.country?.toLowerCase().includes(term)) {
          return false;
        }
      }
      
      // Country filter (with normalization)
      if (countryFilter !== 'all' && normalizeCountry(uni.country) !== countryFilter) {
        return false;
      }
      
      // Intake filter
      if (intakeFilter !== 'all') {
        if (!uni.available_intakes?.includes(intakeFilter)) {
          return false;
        }
      }
      
      return true;
    });
  }, [universities, searchTerm, countryFilter, intakeFilter]);

  // Filter courses
  const filteredCourses = useMemo(() => {
    return courses.filter(course => {
      const uni = universityMap[course.university_id];
      
      // Search term filter
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        if (!course.name.toLowerCase().includes(term) && 
            !course.university_name?.toLowerCase().includes(term) &&
            !course.field_of_study?.toLowerCase().includes(term)) {
          return false;
        }
      }
      
      // Country filter (with normalization)
      if (countryFilter !== 'all' && normalizeCountry(course.country) !== countryFilter) {
        return false;
      }
      
      // University filter
      if (universityFilter !== 'all' && course.university_id !== universityFilter) {
        return false;
      }
      
      // Field filter
      if (fieldFilter !== 'all' && course.field_of_study !== fieldFilter) {
        return false;
      }
      
      // Level filter (with normalization)
      if (levelFilter !== 'all') {
        const normalizeLevel = (level) => {
          if (!level) return null;
          const l = level.toLowerCase().trim();
          if (l === 'bachelors' || l === 'bachelor') return 'Bachelors';
          if (l === 'masters' || l === 'master') return 'Masters';
          if (l === 'phd' || l === 'doctorate' || l === 'doctoral') return 'PhD';
          if (l === 'foundation') return 'Foundation';
          if (l === 'undergraduate') return 'Undergraduate';
          if (l === 'pre-masters') return 'Pre-Masters';
          return level.charAt(0).toUpperCase() + level.slice(1).toLowerCase();
        };
        const courseLevel = normalizeLevel(course.degree_level || course.level);
        if (courseLevel !== levelFilter) {
          return false;
        }
      }
      
      // Intake filter
      if (intakeFilter !== 'all') {
        const intakeDates = Array.isArray(course.intake_dates) 
          ? course.intake_dates 
          : (course.intake_dates || '').split(',').map(d => d.trim());
        if (!intakeDates.includes(intakeFilter)) {
          // Also check university intakes
          if (!uni?.available_intakes?.includes(intakeFilter)) {
            return false;
          }
        }
      }
      
      return true;
    });
  }, [courses, universityMap, searchTerm, countryFilter, universityFilter, intakeFilter, fieldFilter, levelFilter]);

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setCountryFilter('all');
    setUniversityFilter('all');
    setIntakeFilter('all');
    setFieldFilter('all');
    setLevelFilter('all');
    setCourses(initialCourses || []);
    setTotalLabel('');
  };

  const hasActiveFilters = countryFilter !== 'all' || universityFilter !== 'all' || 
    intakeFilter !== 'all' || fieldFilter !== 'all' || levelFilter !== 'all' || searchTerm;

  // Format currency
  const formatCurrency = (amount, currency = 'GBP') => {
    const symbols = { GBP: '£', USD: '$', EUR: '€', CAD: 'C$' };
    const num = parseFloat(amount);
    if (isNaN(num)) return amount;
    return `${symbols[currency] || currency}${num.toLocaleString()}`;
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-5 text-white">
        <div className="flex items-center gap-3 mb-2">
          <BookOpen className="w-7 h-7" />
          <h2 className="text-xl font-bold">Browse Universities & Courses</h2>
        </div>
        <p className="text-blue-100 text-sm">
          Find courses for your students and see commission rates per university {totalLabel && `• ${totalLabel}`}
          {searching && <Loader2 className="w-4 h-4 inline ml-2 animate-spin" />}
        </p>
      </div>

      {/* Search & Filter Bar */}
      <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search universities or courses..."
                className="pl-10"
                data-testid="course-browser-search"
              />
            </div>
          </div>
          
          {/* Quick Filters */}
          <div className="flex flex-wrap items-center gap-2">
            <Select value={countryFilter} onValueChange={setCountryFilter}>
              <SelectTrigger className="w-[140px]" data-testid="filter-country">
                <Globe className="w-4 h-4 mr-1 text-gray-500" />
                <SelectValue placeholder="Country" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Countries</SelectItem>
                {filterOptions.countries.map(country => (
                  <SelectItem key={country} value={country}>{country}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={intakeFilter} onValueChange={setIntakeFilter}>
              <SelectTrigger className="w-[150px]" data-testid="filter-intake">
                <Calendar className="w-4 h-4 mr-1 text-gray-500" />
                <SelectValue placeholder="Intake" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Intakes</SelectItem>
                {filterOptions.intakes.map(intake => (
                  <SelectItem key={intake} value={intake}>{intake}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className={showFilters ? 'bg-teal-50 border-teal-300' : ''}
              data-testid="more-filters-btn"
            >
              <Filter className="w-4 h-4 mr-1" />
              More Filters
            </Button>
            
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <X className="w-4 h-4 mr-1" />
                Clear
              </Button>
            )}
          </div>
        </div>
        
        {/* Extended Filters */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-slate-700 grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs text-gray-500 mb-1">University</Label>
              <Select value={universityFilter} onValueChange={setUniversityFilter}>
                <SelectTrigger data-testid="filter-university">
                  <SelectValue placeholder="All Universities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Universities</SelectItem>
                  {filteredUniversities.map(uni => (
                    <SelectItem key={uni.id} value={uni.id}>{uni.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-xs text-gray-500 mb-1">Field of Study</Label>
              <Select value={fieldFilter} onValueChange={setFieldFilter}>
                <SelectTrigger data-testid="filter-field">
                  <SelectValue placeholder="All Fields" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Fields</SelectItem>
                  {filterOptions.fields.map(field => (
                    <SelectItem key={field} value={field}>{field}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-xs text-gray-500 mb-1">Degree Level</Label>
              <Select value={levelFilter} onValueChange={setLevelFilter}>
                <SelectTrigger data-testid="filter-level">
                  <SelectValue placeholder="All Levels" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Levels</SelectItem>
                  {filterOptions.levels.map(level => (
                    <SelectItem key={level} value={level}>{level}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </div>

      {/* View Mode Tabs */}
      <Tabs value={viewMode} onValueChange={setViewMode}>
        <TabsList className="bg-gray-100 dark:bg-slate-700 p-1">
          <TabsTrigger 
            value="courses" 
            className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-800"
            data-testid="view-courses-tab"
          >
            <GraduationCap className="w-4 h-4 mr-2" />
            Courses ({filteredCourses.length})
          </TabsTrigger>
          <TabsTrigger 
            value="universities" 
            className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-800"
            data-testid="view-universities-tab"
          >
            <Building className="w-4 h-4 mr-2" />
            Universities ({filteredUniversities.length})
          </TabsTrigger>
        </TabsList>

        {/* Courses View */}
        <TabsContent value="courses" className="mt-4">
          {filteredCourses.length === 0 ? (
            <div className="text-center py-12 bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700">
              <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500">No courses found matching your filters</p>
              {hasActiveFilters && (
                <Button variant="link" onClick={clearFilters} className="text-teal-600 mt-2">
                  Clear all filters
                </Button>
              )}
            </div>
          ) : (
            <div className="grid gap-4">
              {filteredCourses.slice(0, 50).map(course => {
                const uni = universityMap[course.university_id];
                return (
                  <div 
                    key={course.id} 
                    className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-4 hover:border-teal-300 transition-colors"
                    data-testid={`course-card-${course.id}`}
                  >
                    <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-lg bg-indigo-100 dark:bg-indigo-900 flex items-center justify-center flex-shrink-0">
                            <GraduationCap className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-gray-800 dark:text-white font-medium text-lg">
                              {course.name}
                            </h4>
                            <p className="text-gray-500 dark:text-gray-400 text-sm flex items-center gap-1 mt-1">
                              <Building className="w-3 h-3" />
                              {course.university_name || uni?.name || 'Unknown University'}
                            </p>
                            <p className="text-gray-400 text-xs flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {course.country || uni?.country}
                            </p>
                          </div>
                        </div>
                        
                        {/* Badges Row */}
                        <div className="flex flex-wrap gap-2 mt-3 ml-13">
                          {course.field_of_study && (
                            <Badge className="bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 text-xs">
                              {course.field_of_study}
                            </Badge>
                          )}
                          {(course.degree_level || course.level) && (
                            <Badge className="bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 text-xs">
                              {course.degree_level || course.level}
                            </Badge>
                          )}
                          {course.intake_dates && (
                            <Badge className="bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 text-xs">
                              <Calendar className="w-3 h-3 mr-1" />
                              {Array.isArray(course.intake_dates) ? course.intake_dates[0] : course.intake_dates.split(',')[0]}
                            </Badge>
                          )}
                          {/* Commission Badge */}
                          {uni?.commission_percentage && (
                            <Badge className="bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300 text-xs font-semibold">
                              <Percent className="w-3 h-3 mr-1" />
                              {uni.commission_percentage}% commission
                            </Badge>
                          )}
                        </div>
                        
                        {/* Details Row */}
                        <div className="flex flex-wrap items-center gap-4 mt-3 ml-13 text-sm">
                          {course.tuition_fee && (
                            <span className="text-green-600 dark:text-green-400 font-semibold flex items-center gap-1">
                              <DollarSign className="w-4 h-4" />
                              {formatCurrency(course.tuition_fee, course.currency)}
                              {course.duration_months && <span className="text-gray-400 font-normal">/ {course.duration_months}mo</span>}
                            </span>
                          )}
                          {course.ielts_requirement && (
                            <span className="text-gray-500 dark:text-gray-400">
                              IELTS: {course.ielts_requirement}+
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex flex-col gap-2">
                        {selectedStudent && onApplyForStudent && (
                          <Button
                            size="sm"
                            onClick={() => onApplyForStudent(course.id)}
                            className="bg-teal-600 hover:bg-teal-700 text-white"
                            data-testid={`apply-course-${course.id}`}
                          >
                            Apply for Student
                          </Button>
                        )}
                        {course.course_url && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => window.open(course.course_url, '_blank')}
                            className="border-gray-300"
                          >
                            <ExternalLink className="w-4 h-4 mr-1" />
                            View Details
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              {filteredCourses.length > 50 && (
                <p className="text-center text-gray-500 text-sm py-4">
                  Showing 50 of {filteredCourses.length} courses. Use filters to narrow down results.
                </p>
              )}
            </div>
          )}
        </TabsContent>

        {/* Universities View */}
        <TabsContent value="universities" className="mt-4">
          {filteredUniversities.length === 0 ? (
            <div className="text-center py-12 bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700">
              <Building className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500">No universities found matching your filters</p>
              {hasActiveFilters && (
                <Button variant="link" onClick={clearFilters} className="text-teal-600 mt-2">
                  Clear all filters
                </Button>
              )}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredUniversities.map(uni => {
                const uniCourses = courses.filter(c => c.university_id === uni.id);
                return (
                  <div 
                    key={uni.id} 
                    className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-5 hover:border-teal-300 transition-colors"
                    data-testid={`university-card-${uni.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 rounded-lg bg-blue-100 dark:bg-blue-900 flex items-center justify-center flex-shrink-0">
                        <Building className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-gray-800 dark:text-white font-medium truncate">
                          {uni.name}
                        </h4>
                        <p className="text-gray-500 dark:text-gray-400 text-sm flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {uni.city}, {uni.country}
                        </p>
                      </div>
                    </div>
                    
                    {/* Info Badges */}
                    <div className="flex flex-wrap gap-2 mt-4">
                      {uni.ranking && (
                        <Badge className="bg-teal-100 dark:bg-teal-900/50 text-teal-700 dark:text-teal-300 text-xs">
                          <TrendingUp className="w-3 h-3 mr-1" />
                          Rank #{uni.ranking}
                        </Badge>
                      )}
                      <Badge className="bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 text-xs">
                        <GraduationCap className="w-3 h-3 mr-1" />
                        {uniCourses.length} courses
                      </Badge>
                    </div>
                    
                    {/* Commission Info - Highlighted */}
                    {uni.commission_percentage ? (
                      <div className="mt-4 p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800">
                        <div className="flex items-center gap-2 text-orange-700 dark:text-orange-300">
                          <Percent className="w-4 h-4" />
                          <span className="font-semibold">{uni.commission_percentage}% Commission</span>
                        </div>
                        <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">
                          Your share: {uni.agent_share_percentage || 70}% ({((uni.commission_percentage * (uni.agent_share_percentage || 70)) / 100).toFixed(1)}% of fee)
                        </p>
                      </div>
                    ) : (
                      <div className="mt-4 p-3 bg-gray-50 dark:bg-slate-700 rounded-lg">
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Commission rate not set - contact H E Direct
                        </p>
                      </div>
                    )}
                    
                    {/* Available Intakes */}
                    {uni.available_intakes?.length > 0 && (
                      <div className="mt-3">
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Available Intakes:</p>
                        <div className="flex flex-wrap gap-1">
                          {uni.available_intakes.slice(0, 3).map(intake => (
                            <Badge key={intake} className="bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 text-xs">
                              {intake}
                            </Badge>
                          ))}
                          {uni.available_intakes.length > 3 && (
                            <Badge className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs">
                              +{uni.available_intakes.length - 3} more
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* View Courses Button */}
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full mt-4 border-gray-300 dark:border-slate-600"
                      onClick={() => {
                        setUniversityFilter(uni.id);
                        setViewMode('courses');
                      }}
                      data-testid={`view-uni-courses-${uni.id}`}
                    >
                      View {uniCourses.length} Courses
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CourseBrowser;
